import { useEffect, useRef } from "react";
import { useLocation, useParams } from "react-router-dom";

const TRACK_URL = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/track-pageview`;
const ANON_KEY = import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY;

/**
 * Analytics läuft NICHT im kritischen Pfad:
 * - kein Supabase-Client-Import (verhindert Chunk-Kopplung mit dem Hero)
 * - startet frühestens per requestIdleCallback nach dem LCP
 * - benötigt weder Realtime noch WebSocket
 */
function scheduleIdle(cb: () => void, timeout = 2500) {
  if (typeof window === "undefined") return;
  const w = window as unknown as {
    requestIdleCallback?: (cb: () => void, opts?: { timeout: number }) => number;
  };
  if (typeof w.requestIdleCallback === "function") {
    w.requestIdleCallback(cb, { timeout });
  } else {
    setTimeout(cb, 1500);
  }
}

export function useAnalytics() {
  const location = useLocation();
  const params = useParams();
  const lastTracked = useRef<string>("");

  useEffect(() => {
    if (location.pathname.startsWith("/admin") || location.pathname.startsWith("/login")) return;
    if (typeof navigator !== "undefined" && (navigator as { doNotTrack?: string }).doNotTrack === "1") return;

    const pathKey = location.pathname + location.search;
    if (lastTracked.current === pathKey) return;
    lastTracked.current = pathKey;

    let cancelled = false;
    scheduleIdle(() => {
      if (cancelled) return;
      const apartmentId =
        location.pathname.match(/^\/ferienwohnungen\/[^/]+\/([0-9a-f-]{36})$/i)?.[1] ?? null;

      const payload = JSON.stringify({
        path: location.pathname,
        apartment_id: apartmentId,
        referrer: document.referrer || null,
        language: document.documentElement.lang || navigator.language?.slice(0, 2) || null,
      });


      try {
        fetch(TRACK_URL, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            apikey: ANON_KEY,
            Authorization: `Bearer ${ANON_KEY}`,
          },
          body: payload,
          keepalive: true,
        }).catch(() => {});
      } catch {
        // silent — analytics never breaks UX
      }
    });

    return () => {
      cancelled = true;
    };
  }, [location.pathname, location.search, params]);
}
